import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {BreadCrumb} from "primereact/breadcrumb";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";
import PageHeaderButton from "../components/page-header-button";
import {useChat} from "../../models/chat/use-chat";
import {useChatParticipant} from "../../models/chat-participant/use-chat-participant";
import useUserEventLog from "../../models/user-event-log/use-user-event-log";
import ConversationsTab from "./tabs/conversations";
import ActivityTab from "./tabs/activity";
import MyWorkTab from "./tabs/my-work";
import {SelectButton} from "primereact/selectbutton";
import IncomingTab from "./tabs/incoming";
import {isActivityItem, isIncomingItem} from "./inbox-utils";
import "./inbox.css";
import PushPromptBanner from "../../system/push/push-prompt-banner";
import {
    setWorkspaceGraphChatMessages,
    setWorkspaceGraphChatParticipants,
    setWorkspaceGraphChats,
    setWorkspaceGraphInboxItems,
} from "../../system/workspace-graph-slice";
import {useWorkspaceRole} from "../../system/use-workspace-role";

function LoadingPanel() {
    return (
        <div className="h-full flex align-items-center justify-content-center">
            <ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/>
        </div>
    );
}

// Activity tab (spec section 3): the tasks assigned to me come first; the old event timeline stays one click away.
function ActivityPanel(props) {
    const [view, setView] = useState("work");
    return (
        <div className="h-full min-h-0 flex flex-column overflow-hidden">
            <div className="my-work-switch">
                <SelectButton
                    value={view}
                    allowEmpty={false}
                    onChange={(event) => setView(event.value)}
                    options={[{label: "کارهای من", value: "work"}, {label: "رویدادها", value: "events"}]}
                    className="text-xs"
                    pt={{button: {className: "text-xs py-2 px-3"}}}
                />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
                {view === "work" ? <MyWorkTab {...props}/> : <ActivityTab {...props}/>}
            </div>
        </div>
    );
}

const TAB_STORAGE_KEY = "zofino.inbox.tab";
const TAB_ALIASES = {incoming: "notifications", notifications: "notifications", conversations: "conversations", activity: "activity"};

function storedTab() {
    try {
        return window.localStorage.getItem(TAB_STORAGE_KEY);
    } catch (error) {
        return null;
    }
}

function Inbox(props) {
    const [activeTabId, setActiveTabId] = useState(() => TAB_ALIASES[storedTab()] || "notifications");
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const translate = useTranslate();
    const chatApi = useChat();
    const participantApi = useChatParticipant();
    const eventApi = useUserEventLog();
    const {role: workspaceRole} = useWorkspaceRole();
    const isGuest = workspaceRole === "guest";
    const {chatParticipants, inboxItems} = useSelector((state) => state.workspaceGraph);
    const workspaceKey = useSelector((state) => (
        state.app.workspaceMembershipData?.workspace?.documentId
        ?? state.app.workspaceMembershipData?.workspace?.id
    ));
    const requestedTab = useMemo(() => new URLSearchParams(location.search).get("tab"), [location.search]);
    const requestedChatDocumentId = useMemo(() => (
        new URLSearchParams(location.search).get("chat")
    ), [location.search]);

    useEffect(() => {
        if (TAB_ALIASES[requestedTab]) setActiveTabId(TAB_ALIASES[requestedTab]);
    }, [requestedTab]);

    const selectTab = (id) => {
        setActiveTabId(id);
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, id);
        } catch (error) {
            // per-viewer convenience only
        }
    };

    const refresh = useCallback(async () => {
        setInitialLoading(true);
        setLoadError(null);
        dispatch(setWorkspaceGraphChats([]));
        dispatch(setWorkspaceGraphChatMessages([]));
        dispatch(setWorkspaceGraphChatParticipants([]));
        dispatch(setWorkspaceGraphInboxItems([]));

        const requests = [
            chatApi.getChats(),
            participantApi.getChatParticipants(),
        ];
        if (!isGuest) requests.push(eventApi.getInboxItems());
        const results = await Promise.allSettled(requests);
        const failed = results.find((result) => result.status === "rejected");
        if (failed) setLoadError(failed.reason?.message || "دریافت اطلاعات پیام‌ها ناموفق بود.");
        setInitialLoading(false);
        // API hooks intentionally expose new function identities per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isGuest, workspaceKey]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const conversationUnread = useMemo(() => (
        (chatParticipants || []).reduce((total, item) => total + Number(item?.unreadCount || 0), 0)
    ), [chatParticipants]);
    const incomingUnread = useMemo(() => (
        (inboxItems || []).filter((item) => isIncomingItem(item) && !item.isRead).length
    ), [inboxItems]);
    const activityUnread = useMemo(() => (
        (inboxItems || []).filter((item) => isActivityItem(item) && !item.isRead).length
    ), [inboxItems]);
    // «اعلان‌ها» section: actionable notifications first, chat and the FYI feed one click away (task 12)
    const availableTabs = [
        {
            id: "notifications",
            label: `اعلان‌ها${incomingUnread > 0 ? ` (${incomingUnread})` : ""}`,
            icon: "mi mi:notifications",
            view: IncomingTab,
        },
        {
            id: "conversations",
            label: `گفتگوها${conversationUnread > 0 ? ` (${conversationUnread})` : ""}`,
            icon: "mi mi:chat_bubble",
            view: ConversationsTab,
        },
        {
            id: "activity",
            label: `فعالیت‌ها${activityUnread > 0 ? ` (${activityUnread})` : ""}`,
            icon: "mi mi:bolt",
            view: ActivityPanel,
        },
    ];
    const tabs = isGuest ? availableTabs.filter((tab) => tab.id === "conversations") : availableTabs;
    const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTabId));
    const breadCrumb = [
        {label: translate(props.name || "inbox")},
        {label: tabs[activeIndex]?.label?.replace(/\s*\(\d+\)$/, "")},
    ];

    return (
        <div className="h-full min-h-0 flex flex-column overflow-hidden pb-3" dir="rtl">
            <BreadCrumb
                separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>}
                className="bg-transparent border-none text-xs"
                model={breadCrumb}
            />
            <PageHeaderButton
                title={props.name || "inbox"}
                description={props.description}
                end={(
                    <div className="flex align-items-center gap-2">
                    {!isGuest && (
                        <Button
                            label="تنظیمات اعلان‌ها"
                            icon={<Icon icon="mi mi:tune" className="text-lg ml-1"/>}
                            className="text-xs gap-1"
                            text
                            onClick={() => navigate("/notification-settings")}
                        />
                    )}
                    <Button
                        label="تازه‌سازی"
                        icon={<Icon icon="mi mi:refresh" className="text-lg ml-1"/>}
                        className="text-xs gap-1"
                        onClick={refresh}
                        loading={initialLoading}
                    />
                    </div>
                )}
            />

            {!isGuest && <div className="px-3"><PushPromptBanner/></div>}
            {loadError && (
                <div className="px-3 pt-2">
                    <Message className="text-xs" severity="error" text={loadError}/>
                </div>
            )}

            <div className="flex-1 min-h-0 relative">
                <TabView
                    lazy
                    activeIndex={activeIndex}
                    onTabChange={(event) => selectTab(tabs[event.index]?.id || "notifications")}
                    panelContainerClassName="flex-1 min-h-0 p-0 border-noround"
                    className="h-full flex flex-column"
                >
                    {tabs.map((tab, tabIndex) => {
                        const TabContent = tab.view;
                        return (
                            <TabPanel
                                key={tab.id}
                                header={tab.label}
                                leftIcon={<Icon icon={tab.icon} className="pl-2"/>}
                                className="h-full text-xs"
                            >
                                {tabIndex === activeIndex && (
                                    <div className="h-full min-h-0 overflow-hidden">
                                        {initialLoading ? (
                                            <LoadingPanel/>
                                        ) : (
                                            <TabContent targetChatDocumentId={requestedChatDocumentId}/>
                                        )}
                                    </div>
                                )}
                            </TabPanel>
                        );
                    })}
                </TabView>
            </div>
        </div>
    );
}

export default Inbox;
